;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:localcoin":true});}catch(e){}}();import { captureException } from '@sentry/nextjs';
import { unstable_cache } from 'next/cache';
import { connection } from 'next/server';
import { cache } from 'react';

const FRESH_FOR_S = 5 * 60;
const MAX_RETRY_DELAY_MS = 1000;

const getRetryDelay = (response?: Response) => {
  const retryAfterSeconds = Number(response?.headers.get('retry-after'));

  return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
    ? Math.min(retryAfterSeconds * 1000, MAX_RETRY_DELAY_MS)
    : MAX_RETRY_DELAY_MS / 2;
};

const fetchList = async (url: string): Promise<unknown[]> => {
  for (let attempt = 1; ; attempt++) {
    let response: Response | undefined;
    let networkError: unknown;

    try {
      // no-store: the ~3 MB body is over the data cache's 2 MB limit, which throws in dev.
      response = await fetch(url, { cache: 'no-store' });
    } catch (error) {
      networkError = error;
    }

    if (response?.ok) {
      return response.json();
    }

    const isRetryable = !response || response.status === 429 || response.status >= 500;

    if (attempt >= 2 || !isRetryable) {
      const reason = response ? `responded ${response.status}` : 'request failed';
      const error = new Error(`Locations API ${reason}`, { cause: networkError });

      // Reported here because a failed background revalidation is swallowed by Next.
      captureException(error, {
        tags: { locationsApiStatus: response?.status ?? 'network' },
        extra: { host: new URL(url).host, attempt },
      });

      throw error;
    }

    await new Promise((resolve) => setTimeout(resolve, getRetryDelay(response)));
  }
};

// Web streams rather than node:zlib, because utils/index.ts is also bundled for the client.
const gzip = async (text: string) => {
  const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));

  return Buffer.from(await new Response(stream).arrayBuffer()).toString('base64');
};

const gunzip = async (base64: string) => {
  const stream = new Blob([Buffer.from(base64, 'base64')])
    .stream()
    .pipeThrough(new DecompressionStream('gzip'));

  return new Response(stream).text();
};

// Stored gzipped: the raw CA/AU lists are ~3 MB, over the data cache's 2 MB per-entry
// limit, and compress to ~200 KB. A stale entry keeps being served if a refresh fails.
const getCompressedList = unstable_cache(
  async (url: string) => gzip(JSON.stringify(await fetchList(url))),
  ['atm-locations-gzip'],
  { revalidate: FRESH_FOR_S },
);

// cache() so a page and its generateMetadata unzip and parse the list once per request.
export const fetchLocationsList = cache(async (url: string): Promise<unknown[]> => {
  // The no-store fetch used to keep these routes dynamic; inside unstable_cache it no
  // longer does, and Next would prerender ATM lists and the sitemap at build time.
  await connection();

  return JSON.parse(await gunzip(await getCompressedList(url)));
});
